import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { getCvssSeverityLabelLower } from '../../utils/severity';

/* ═══════════════════════════════════════════════════════════
   CVSS 3.1 metric definitions
   ═══════════════════════════════════════════════════════════ */

interface MetricOption {
  label: string;
  abbr: string;
  tooltip: string;
}

export interface MetricDef {
  name: string;
  abbr: string;
  options: MetricOption[];
}

export const BASE_METRICS: MetricDef[] = [
  {
    name: 'Attack Vector',
    abbr: 'AV',
    options: [
      { label: 'Network', abbr: 'N', tooltip: 'Angriff über das Netzwerk möglich' },
      { label: 'Adjacent', abbr: 'A', tooltip: 'Angriff nur im angrenzenden Netzwerk' },
      { label: 'Local', abbr: 'L', tooltip: 'Lokaler Zugriff erforderlich' },
      { label: 'Physical', abbr: 'P', tooltip: 'Physischer Zugriff erforderlich' },
    ],
  },
  {
    name: 'Attack Complexity',
    abbr: 'AC',
    options: [
      { label: 'Low', abbr: 'L', tooltip: 'Keine besonderen Bedingungen erforderlich' },
      { label: 'High', abbr: 'H', tooltip: 'Besondere Bedingungen müssen erfüllt sein' },
    ],
  },
  {
    name: 'Privileges Required',
    abbr: 'PR',
    options: [
      { label: 'None', abbr: 'N', tooltip: 'Keine Rechte erforderlich' },
      { label: 'Low', abbr: 'L', tooltip: 'Einfache Benutzerrechte erforderlich' },
      { label: 'High', abbr: 'H', tooltip: 'Administratorrechte erforderlich' },
    ],
  },
  {
    name: 'User Interaction',
    abbr: 'UI',
    options: [
      { label: 'None', abbr: 'N', tooltip: 'Keine Benutzerinteraktion nötig' },
      { label: 'Required', abbr: 'R', tooltip: 'Benutzer muss eine Aktion ausführen' },
    ],
  },
  {
    name: 'Scope',
    abbr: 'S',
    options: [
      { label: 'Unchanged', abbr: 'U', tooltip: 'Auswirkung bleibt im betroffenen System' },
      { label: 'Changed', abbr: 'C', tooltip: 'Auswirkung auf andere Systeme möglich' },
    ],
  },
  {
    name: 'Confidentiality',
    abbr: 'C',
    options: [
      { label: 'None', abbr: 'N', tooltip: 'Keine Auswirkung auf Vertraulichkeit' },
      { label: 'Low', abbr: 'L', tooltip: 'Begrenzte Auswirkung auf Vertraulichkeit' },
      { label: 'High', abbr: 'H', tooltip: 'Vollständiger Verlust der Vertraulichkeit' },
    ],
  },
  {
    name: 'Integrity',
    abbr: 'I',
    options: [
      { label: 'None', abbr: 'N', tooltip: 'Keine Auswirkung auf Integrität' },
      { label: 'Low', abbr: 'L', tooltip: 'Begrenzte Auswirkung auf Integrität' },
      { label: 'High', abbr: 'H', tooltip: 'Vollständiger Verlust der Integrität' },
    ],
  },
  {
    name: 'Availability',
    abbr: 'A',
    options: [
      { label: 'None', abbr: 'N', tooltip: 'Keine Auswirkung auf Verfügbarkeit' },
      { label: 'Low', abbr: 'L', tooltip: 'Begrenzte Auswirkung auf Verfügbarkeit' },
      { label: 'High', abbr: 'H', tooltip: 'Vollständiger Verlust der Verfügbarkeit' },
    ],
  },
];

export const TEMPORAL_METRICS: MetricDef[] = [
  {
    name: 'Exploit Code Maturity',
    abbr: 'E',
    options: [
      { label: 'Not Defined', abbr: 'X', tooltip: 'Nicht definiert' },
      { label: 'Unproven', abbr: 'U', tooltip: 'Kein funktionierender Exploit bekannt' },
      { label: 'PoC', abbr: 'P', tooltip: 'Proof-of-Concept existiert' },
      { label: 'Functional', abbr: 'F', tooltip: 'Funktionierender Exploit verfügbar' },
      { label: 'High', abbr: 'H', tooltip: 'Exploit wird aktiv ausgenutzt' },
    ],
  },
  {
    name: 'Remediation Level',
    abbr: 'RL',
    options: [
      { label: 'Not Defined', abbr: 'X', tooltip: 'Nicht definiert' },
      { label: 'Official Fix', abbr: 'O', tooltip: 'Offizieller Patch verfügbar' },
      { label: 'Temporary Fix', abbr: 'T', tooltip: 'Temporärer Fix verfügbar' },
      { label: 'Workaround', abbr: 'W', tooltip: 'Workaround verfügbar' },
      { label: 'Unavailable', abbr: 'U', tooltip: 'Kein Fix verfügbar' },
    ],
  },
  {
    name: 'Report Confidence',
    abbr: 'RC',
    options: [
      { label: 'Not Defined', abbr: 'X', tooltip: 'Nicht definiert' },
      { label: 'Unknown', abbr: 'U', tooltip: 'Einzelne unbestätigte Berichte' },
      { label: 'Reasonable', abbr: 'R', tooltip: 'Mehrere Berichte oder plausibel' },
      { label: 'Confirmed', abbr: 'C', tooltip: 'Vom Hersteller bestätigt' },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════
   CVSS 3.1 scoring engine
   ═══════════════════════════════════════════════════════════ */

const METRIC_WEIGHTS: Record<string, Record<string, number>> = {
  AV: { N: 0.85, A: 0.62, L: 0.55, P: 0.20 },
  AC: { L: 0.77, H: 0.44 },
  // PR depends on Scope – handled in code
  PR_U: { N: 0.85, L: 0.62, H: 0.27 },
  PR_C: { N: 0.85, L: 0.68, H: 0.50 },
  UI: { N: 0.85, R: 0.62 },
  C: { N: 0, L: 0.22, H: 0.56 },
  I: { N: 0, L: 0.22, H: 0.56 },
  A: { N: 0, L: 0.22, H: 0.56 },
  // Temporal
  E: { X: 1, U: 0.91, P: 0.94, F: 0.97, H: 1 },
  RL: { X: 1, O: 0.95, T: 0.96, W: 0.97, U: 1 },
  RC: { X: 1, U: 0.92, R: 0.96, C: 1 },
};

/** Round up to 1 decimal place (CVSS spec "roundUp") */
function roundUp(x: number): number {
  return Math.ceil(x * 10) / 10;
}

type MetricValues = Record<string, string>;

function calcBaseScore(m: MetricValues): number | null {
  // Check all required base metrics are set
  for (const def of BASE_METRICS) {
    if (!m[def.abbr]) return null;
  }

  const scopeChanged = m.S === 'C';
  const prKey = scopeChanged ? 'PR_C' : 'PR_U';

  const iss =
    1 -
    (1 - METRIC_WEIGHTS.C[m.C]) *
      (1 - METRIC_WEIGHTS.I[m.I]) *
      (1 - METRIC_WEIGHTS.A[m.A]);

  let impact: number;
  if (scopeChanged) {
    impact =
      7.52 * (iss - 0.029) -
      3.25 * Math.pow(iss - 0.02, 15);
  } else {
    impact = 6.42 * iss;
  }

  if (impact <= 0) return 0;

  const exploitability =
    8.22 *
    METRIC_WEIGHTS.AV[m.AV] *
    METRIC_WEIGHTS.AC[m.AC] *
    METRIC_WEIGHTS[prKey][m.PR] *
    METRIC_WEIGHTS.UI[m.UI];

  let baseScore: number;
  if (scopeChanged) {
    baseScore = roundUp(Math.min(1.08 * (impact + exploitability), 10));
  } else {
    baseScore = roundUp(Math.min(impact + exploitability, 10));
  }

  return baseScore;
}

function calcTemporalScore(
  baseScore: number,
  m: MetricValues,
): number {
  const e = METRIC_WEIGHTS.E[m.E || 'X'] ?? 1;
  const rl = METRIC_WEIGHTS.RL[m.RL || 'X'] ?? 1;
  const rc = METRIC_WEIGHTS.RC[m.RC || 'X'] ?? 1;

  // If all temporal are "Not Defined", temporal = base
  if ((m.E || 'X') === 'X' && (m.RL || 'X') === 'X' && (m.RC || 'X') === 'X') {
    return baseScore;
  }

  return roundUp(baseScore * e * rl * rc);
}

function buildVectorString(m: MetricValues): string {
  let vector = 'CVSS:3.1';
  for (const def of BASE_METRICS) {
    if (m[def.abbr]) {
      vector += `/${def.abbr}:${m[def.abbr]}`;
    }
  }
  // Only include temporal metrics that are not "X"
  for (const def of TEMPORAL_METRICS) {
    if (m[def.abbr] && m[def.abbr] !== 'X') {
      vector += `/${def.abbr}:${m[def.abbr]}`;
    }
  }
  return vector;
}

export function parseVectorString(vector: string): MetricValues {
  const result: MetricValues = {};
  if (!vector) return result;

  const parts = vector.replace(/^CVSS:3\.[01]\//, '').split('/');
  for (const part of parts) {
    const [key, value] = part.split(':');
    if (key && value) {
      result[key] = value;
    }
  }
  return result;
}

/* ═══════════════════════════════════════════════════════════
   Severity helpers
   ═══════════════════════════════════════════════════════════ */

function getSeverityColor(score: number): string {
  if (score === 0) return '#78909c';
  if (score <= 3.9) return '#4caf50';
  if (score <= 6.9) return '#ff9800';
  if (score <= 8.9) return '#f44336';
  return '#9c27b0';
}

/* ═══════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════ */

interface CvssCalculatorProps {
  open: boolean;
  onClose: () => void;
  onApply: (score: number, vector: string) => void;
  initialVector?: string;
}

const CvssCalculator: React.FC<CvssCalculatorProps> = ({
  open,
  onClose,
  onApply,
  initialVector,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  /* Theme-aware Farben */
  const cx = {
    textPrimary: isDark ? '#e8edf3' : '#1a2332',
    textSecondary: isDark ? '#8899aa' : '#5f7082',
    textMuted: isDark ? '#556677' : '#8899aa',
    border: isDark ? '#1e3a5f' : '#c8cfd8',
    bgElevated: isDark ? '#2c3358' : '#eef1f5',
    bgInput: isDark ? '#0d1b2a' : '#f5f7fa',
    selectedBg: isDark ? '#1e3a5f' : '#e3f0ff',
    selectedColor: isDark ? '#e8edf3' : '#1a2332',
    selectedHover: isDark ? '#264a78' : '#d0e4ff',
    hoverBg: isDark ? '#2c3358' : '#eef1f5',
    disabledBg: isDark ? '#1e3a5f' : '#dfe3e8',
    disabledColor: isDark ? '#556677' : '#8899aa',
  };

  const [metrics, setMetrics] = useState<MetricValues>({});
  const [showTemporal, setShowTemporal] = useState(false);
  const [pasteValue, setPasteValue] = useState('');
  const [pasteError, setPasteError] = useState('');

  // Parse initial vector when dialog opens
  useEffect(() => {
    if (open) {
      if (initialVector) {
        const parsed = parseVectorString(initialVector);
        setMetrics(parsed);
        const hasTemporal = TEMPORAL_METRICS.some(
          (d) => parsed[d.abbr] && parsed[d.abbr] !== 'X',
        );
        setShowTemporal(hasTemporal);
      } else {
        setMetrics({});
        setShowTemporal(false);
      }
      setPasteValue('');
      setPasteError('');
    }
  }, [open, initialVector]);

  const handleMetricChange = useCallback(
    (metricAbbr: string, value: string | null) => {
      if (value === null) return;
      setMetrics((prev) => ({ ...prev, [metricAbbr]: value }));
    },
    [],
  );

  const applyPastedVector = useCallback(() => {
    const trimmed = pasteValue.trim();
    if (!trimmed) {
      setPasteError('Bitte einen CVSS-Vektor eingeben');
      return;
    }
    if (!/^CVSS:3\.[01]\//.test(trimmed)) {
      setPasteError('Ungültiges Format – erwartet: CVSS:3.1/AV:N/AC:L/...');
      return;
    }
    const parsed = parseVectorString(trimmed);
    const missingBase = BASE_METRICS.filter((d) => !parsed[d.abbr]);
    if (missingBase.length > 0) {
      setPasteError(
        `Fehlende Base-Metriken: ${missingBase.map((d) => d.abbr).join(', ')}`,
      );
      return;
    }
    setPasteError('');
    setMetrics(parsed);
    setPasteValue('');
    const hasTemporal = TEMPORAL_METRICS.some(
      (d) => parsed[d.abbr] && parsed[d.abbr] !== 'X',
    );
    if (hasTemporal) setShowTemporal(true);
  }, [pasteValue]);

  const baseScore = useMemo(() => calcBaseScore(metrics), [metrics]);
  const finalScore = useMemo(() => {
    if (baseScore === null) return null;
    return calcTemporalScore(baseScore, metrics);
  }, [baseScore, metrics]);

  const vectorString = useMemo(() => {
    if (baseScore === null) return '';
    return buildVectorString(metrics);
  }, [baseScore, metrics]);

  const allBaseSelected = BASE_METRICS.every((d) => !!metrics[d.abbr]);

  const handleApply = () => {
    if (finalScore !== null && vectorString) {
      onApply(finalScore, vectorString);
    }
  };

  const handleCopyVector = () => {
    if (vectorString) {
      navigator.clipboard.writeText(vectorString);
    }
  };

  /* ── Render helpers ──────────────────────────────── */

  const renderMetricRow = (def: MetricDef) => (
    <Box key={def.abbr} sx={{ mb: 2 }}>
      <Typography
        variant="body2"
        sx={{ color: cx.textSecondary, mb: 0.5, fontWeight: 600 }}
      >
        {def.name} ({def.abbr})
      </Typography>
      <ToggleButtonGroup
        exclusive
        value={metrics[def.abbr] || null}
        onChange={(_, val) => handleMetricChange(def.abbr, val)}
        size="small"
        sx={{ flexWrap: 'wrap', gap: 0.5 }}
      >
        {def.options.map((opt) => (
          <Tooltip key={opt.abbr} title={opt.tooltip} arrow>
            <ToggleButton
              value={opt.abbr}
              sx={{
                px: 1.5,
                py: 0.5,
                fontSize: '0.8rem',
                textTransform: 'none',
                color: cx.textSecondary,
                borderColor: cx.border,
                '&.Mui-selected': {
                  bgcolor: cx.selectedBg,
                  color: cx.selectedColor,
                  borderColor: '#2979ff',
                  '&:hover': { bgcolor: cx.selectedHover },
                },
                '&:hover': { bgcolor: cx.hoverBg },
              }}
            >
              {opt.label} ({opt.abbr})
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: cx.textPrimary,
          fontWeight: 700,
          pr: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          CVSS 3.1 Calculator
          {finalScore !== null && (
            <Chip
              label={`${finalScore.toFixed(1)} ${getCvssSeverityLabelLower(finalScore)}`}
              sx={{
                bgcolor: getSeverityColor(finalScore),
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.9rem',
              }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: cx.textSecondary }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {/* ── Paste vector input ───────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: cx.bgInput,
            border: `1px solid ${cx.border}`,
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H"
            label="Vektor einfügen"
            value={pasteValue}
            onChange={(e) => {
              setPasteValue(e.target.value);
              if (pasteError) setPasteError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyPastedVector();
            }}
            error={!!pasteError}
            helperText={pasteError || undefined}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                color: cx.textPrimary,
              },
            }}
          />
          <Tooltip title="Vektor übernehmen">
            <IconButton
              onClick={applyPastedVector}
              size="small"
              sx={{ color: cx.textSecondary, mt: 0.5 }}
            >
              <ContentPasteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Score display ──────────────────────── */}
        {vectorString && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 3,
              p: 1.5,
              borderRadius: 1,
              bgcolor: cx.bgInput,
              border: `1px solid ${cx.border}`,
            }}
          >
            <TextField
              fullWidth
              size="small"
              value={vectorString}
              InputProps={{ readOnly: true }}
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  color: cx.textPrimary,
                },
              }}
            />
            <Tooltip title="Vektor kopieren">
              <IconButton onClick={handleCopyVector} size="small" sx={{ color: cx.textSecondary }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* ── Base Metrics ───────────────────────── */}
        <Typography
          variant="subtitle1"
          sx={{ color: cx.textPrimary, fontWeight: 700, mb: 2 }}
        >
          Base Score Metriken
          {baseScore !== null && (
            <Typography component="span" sx={{ color: cx.textSecondary, ml: 1, fontWeight: 400 }}>
              (Base Score: {baseScore.toFixed(1)})
            </Typography>
          )}
        </Typography>

        {BASE_METRICS.map(renderMetricRow)}

        {/* ── Temporal toggle ────────────────────── */}
        <Box sx={{ mt: 3, mb: 1 }}>
          <Button
            size="small"
            onClick={() => setShowTemporal((v) => !v)}
            sx={{ color: cx.textSecondary, textTransform: 'none' }}
          >
            {showTemporal ? '▾ Temporal Metriken ausblenden' : '▸ Temporal Metriken einblenden (optional)'}
          </Button>
        </Box>

        {showTemporal && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ color: cx.textPrimary, fontWeight: 700, mb: 2 }}
            >
              Temporal Score Metriken
            </Typography>
            {TEMPORAL_METRICS.map(renderMetricRow)}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          sx={{ color: cx.textSecondary, '&:hover': { bgcolor: cx.hoverBg } }}
        >
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!allBaseSelected}
          sx={{
            bgcolor: '#2979ff',
            '&:hover': { bgcolor: '#1565c0' },
            '&.Mui-disabled': { bgcolor: cx.disabledBg, color: cx.disabledColor },
          }}
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CvssCalculator;
